import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { first, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiReplaceSubjectCategoryRequest, ApiSubjectCategory, ApiSubjectCategoryList, ApiTopic, ProjectSubjectCategoriesClient, SubjectCategoriesClient, SubjectCategoryType } from 'content-manager/src/app/core/open-api/content-manager-api-client';
import { ProjectStateService } from 'content-manager/src/app/projects/project/project-state.service';
import { TableSettings } from 'sc-common';

@Component({
    selector: 'cm-project-level-topics',
    templateUrl: './project-level-topics.component.html',
    standalone: false
})
export class ProjectLevelTopicsComponent implements OnInit, OnDestroy {

    @Input({ required: true }) public isReadonly = false;

    public readonly tableSettings: TableSettings<ApiSubjectCategoryList>;

    public selectedTopicHeadings: string[] = [];

    public requestInProcess = false;

    private _originalSelectedHeadings: string[] = [];

    private readonly _refreshTable$ = new Subject<void>();

    private readonly _destroy$ = new Subject<void>();

    constructor(
        private readonly _projectStateService: ProjectStateService,
        private readonly _subjectCategoriesClient: SubjectCategoriesClient,
        private readonly _projectSubjectCategoriesClient: ProjectSubjectCategoriesClient) {

        this.tableSettings = this._initTable();
    }

    public get hasChanges(): boolean {

        if (this._originalSelectedHeadings.length !== this.selectedTopicHeadings.length) {
            return true;
        }

        const sortedOriginal = [...this._originalSelectedHeadings].sort();
        const sortedCurrent = [...this.selectedTopicHeadings].sort();

        return sortedOriginal.some((v, i) => v !== sortedCurrent[i]);
    }

    public get canUpdate(): boolean {
        return !this.isReadonly && this.hasChanges && !this.requestInProcess;
    }

    public ngOnInit(): void {
        this._loadSelection();
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
        this._refreshTable$.complete();
    }

    public update(): void {

        this.requestInProcess = true;

        this._subjectCategoriesClient.getTopics().pipe(
            first(),
            switchMap(topics => this._projectStateService.firstSwitchMap(project =>
                this._projectSubjectCategoriesClient.replaceSubjectCategories(
                    project.id,
                    undefined,
                    new ApiReplaceSubjectCategoryRequest({
                        subjectCategoryType: SubjectCategoryType.Topic,
                        subjectCategories: this._buildSubjectCategories(topics)
                    })))),
            takeUntil(this._destroy$))
            .subscribe(() => {
                this.requestInProcess = false;
                this._originalSelectedHeadings = [...this.selectedTopicHeadings];
                this._refreshTable$.next();
            });
    }

    private _loadSelection(): void {
        this._projectStateService.firstSwitchMap(project =>
            this._projectSubjectCategoriesClient.getSubjectCategoryList(project.id, SubjectCategoryType.Topic, undefined))
            .pipe(takeUntil(this._destroy$))
            .subscribe(result => {
                const headings = result.items?.map(c => c.headingText!).filter(h => !!h) ?? [];
                this.selectedTopicHeadings = [...headings];
                this._originalSelectedHeadings = [...headings];
            });
    }

    private _buildSubjectCategories(topics: ApiTopic[]): ApiSubjectCategory[] {
        return this.selectedTopicHeadings
            .map(heading => topics.find(t => t.heading === heading))
            .filter((t): t is ApiTopic => !!t)
            .map(topic => new ApiSubjectCategory({
                subjectCategoryType: SubjectCategoryType.Topic,
                headingText: topic.heading
            }));
    }

    private _initTable(): TableSettings<ApiSubjectCategoryList> {
        return TableSettings.create(ApiSubjectCategoryList, {
            dataSource: oDataQuery => this._projectStateService.switchMap(project =>
                this._projectSubjectCategoriesClient.getSubjectCategoryList(project.id, SubjectCategoryType.Topic, oDataQuery)),
            columnResizeMode: 'fit',
            paginator: false,
            emptyMessageText: $localize`No topics assigned`,
            reload$: this._refreshTable$.asObservable(),
            columns: {
                code: {
                    hidden: true
                },
                headingText: {
                    header: $localize`Topics`
                }
            },
            hasFilters: false,
            defaultSort: {
                field: x => x.headingText
            }
        });
    }
}
